// Search data loader with fallback

export interface SearchResult {
  type: 'cysteine' | 'protein';
  score: number;
  primary: string;
  secondary: string;
  tertiary?: string;
  index: number;
}

export interface SearchDataStructure {
  c: Array<[string, string, number, string, string]>; // cysteines
  p: Array<[string, string, string, number, string]>; // proteins (entry, entryName, geneNames, cysteineCount, proteinNames)
  ci: Record<string, number[]>; // cysteine index
  pi: Record<string, number[]>; // protein index
  pf: {
    c: Record<string, number[]>; // cysteine prefix index
    p: Record<string, number[]>; // protein prefix index
  };
  m: {
    v: string;
    d: string;
    cc: number;
    pc: number;
  };
}

// Cache
let searchData: SearchDataStructure | null = null;
let isLoaded = false;

// Load search data with multiple fallback strategies
export async function loadSearchData(): Promise<void> {
  if (isLoaded) return;
  
  try {
    // Try to fetch the pre-built JSON file
    const response = await fetch('/data/searchData.json');
    if (response.ok) {
      searchData = await response.json();
      isLoaded = true;
      console.log('Loaded search data successfully');
      return;
    }
  } catch (e) {
    console.log('Could not load pre-built search data, trying fallback...');
  }
  
  try {
    // Fallback: try to load from src/data
    const response = await fetch('/src/data/searchData.json');
    if (response.ok) {
      searchData = await response.json();
      isLoaded = true;
      console.log('Loaded search data from src');
      return;
    }
  } catch (e) {
    console.log('Could not load search data from src, using empty data');
  }
  
  // Final fallback: empty data
  console.warn('Using empty search data - run npm run build-data to generate search data');
  searchData = {
    c: [],
    p: [],
    ci: {},
    pi: {},
    pf: { c: {}, p: {} },
    m: { v: '1.0', d: new Date().toISOString(), cc: 0, pc: 0 }
  };
  isLoaded = true;
}

// Fast search implementation
export function search(query: string, mode: 'cysteine' | 'protein' | 'all'): SearchResult[] {
  if (!isLoaded || !searchData || !query) return [];
  
  const normalizedQuery = query.toLowerCase().trim();
  const tokens = normalizedQuery.split(/[\s_-]+/);
  const results = new Map<string, SearchResult>();
  
  // Helper to add result
  const addResult = (type: 'c' | 'p', idx: number, score: number) => {
    const key = `${type}-${idx}`;
    if (results.has(key)) {
      const existing = results.get(key)!;
      if (existing.score < score) {
        existing.score = score;
      }
      return;
    }
    
    if (type === 'c' && (mode === 'cysteine' || mode === 'all')) {
      if (idx >= searchData!.c.length) return;
      const [identifier, geneName, isHyper] = searchData!.c[idx];
      results.set(key, {
        type: 'cysteine',
        score,
        primary: identifier,
        secondary: geneName,
        tertiary: isHyper ? 'Hyper-reactive' : undefined,
        index: idx
      });
    } else if (type === 'p' && (mode === 'protein' || mode === 'all')) {
      if (idx >= searchData!.p.length) return;
      const [entry, entryName, geneNames, cysteineCount, proteinNames] = searchData!.p[idx];
      
      // Create display text prioritizing gene name, then protein name
      let displayText = geneNames || entryName;
      if (proteinNames) {
        // Extract the first protein name (before semicolon if multiple)
        const firstProteinName = proteinNames.split(';')[0].trim();
        if (firstProteinName && firstProteinName !== displayText) {
          displayText = geneNames ? `${geneNames} - ${firstProteinName}` : firstProteinName;
        }
      }
      
      results.set(key, {
        type: 'protein',
        score,
        primary: entry,
        secondary: displayText,
        tertiary: cysteineCount > 0 ? `${cysteineCount} cysteine${cysteineCount > 1 ? 's' : ''}` : undefined,
        index: idx
      });
    }
  };
  
  // Search strategy:
  // 1. Check prefix index for autocomplete (highest score)
  if (normalizedQuery.length <= 3 && searchData.pf) {
    if (searchData.pf.c && searchData.pf.c[normalizedQuery]) {
      searchData.pf.c[normalizedQuery].forEach(idx => addResult('c', idx, 100));
    }
    if (searchData.pf.p && searchData.pf.p[normalizedQuery]) {
      searchData.pf.p[normalizedQuery].forEach(idx => addResult('p', idx, 100));
    }
  }
  
  // 2. Check full token matches (high score)
  tokens.forEach(token => {
    if (searchData!.ci && searchData!.ci[token]) {
      searchData!.ci[token].forEach(idx => addResult('c', idx, 90));
    }
    if (searchData!.pi && searchData!.pi[token]) {
      searchData!.pi[token].forEach(idx => addResult('p', idx, 90));
    }
  });
  
  // 3. Check prefix matches on longer queries
  if (normalizedQuery.length > 3 && searchData.pf) {
    const prefix = normalizedQuery.substring(0, 3);
    if (searchData.pf.c && searchData.pf.c[prefix]) {
      searchData.pf.c[prefix].forEach(idx => {
        if (idx >= searchData!.c.length) return;
        const [identifier, geneName] = searchData!.c[idx];
        if (identifier.toLowerCase().startsWith(normalizedQuery) || 
            geneName.toLowerCase().startsWith(normalizedQuery)) {
          addResult('c', idx, 85);
        }
      });
    }
    if (searchData.pf.p && searchData.pf.p[prefix]) {
      searchData.pf.p[prefix].forEach(idx => {
        if (idx >= searchData!.p.length) return;
        const [entry, entryName, geneNames, , proteinNames] = searchData!.p[idx];
        if (entry.toLowerCase().startsWith(normalizedQuery) || 
            entryName.toLowerCase().startsWith(normalizedQuery) ||
            geneNames.toLowerCase().includes(normalizedQuery) ||
            (proteinNames && proteinNames.toLowerCase().includes(normalizedQuery))) {
          addResult('p', idx, 85);
        }
      });
    }
  }
  
  // 4. If few results, do substring search on indexed tokens
  if (results.size < 5) {
    const searchIndices = (index: Record<string, number[]>, type: 'c' | 'p') => {
      if (!index) return;
      Object.entries(index).forEach(([token, indices]) => {
        if (token.includes(normalizedQuery)) {
          indices.slice(0, 10).forEach(idx => addResult(type, idx, 70));
        }
      });
    };
    
    if ((mode === 'cysteine' || mode === 'all') && searchData.ci) {
      searchIndices(searchData.ci, 'c');
    }
    if ((mode === 'protein' || mode === 'all') && searchData.pi) {
      searchIndices(searchData.pi, 'p');
    }
  }
  
  // Sort by score and return top results
  return Array.from(results.values())
    .sort((a, b) => b.score - a.score)
    .slice(0, 20);
}

// Get a random cysteine
export function getRandomCysteine(): SearchResult | null {
  if (!searchData || !searchData.c || searchData.c.length === 0) return null;
  
  const randomIndex = Math.floor(Math.random() * searchData.c.length);
  const [identifier, geneName, isHyper] = searchData.c[randomIndex];
  
  return {
    type: 'cysteine',
    score: 100,
    primary: identifier,
    secondary: geneName,
    tertiary: isHyper ? 'Hyper-reactive' : undefined,
    index: randomIndex
  };
}

// Get detailed information
export function getCysteineDetails(index: number) {
  if (!searchData || index >= searchData.c.length) return null;
  
  const [identifier, geneName, isHyper, proteinId] = searchData.c[index];
  const position = identifier.split('_')[1]; // Extract position from identifier
  return {
    identifier,
    geneName,
    isHyperReactive: isHyper === 1,
    proteinId,
    position,
    altIdentifier: `${geneName}_${position}`
  };
}

export function getProteinDetails(index: number) {
  if (!searchData || index >= searchData.p.length) return null;
  
  const [entry, , geneNames, cysteineCount, proteinNames] = searchData.p[index];
  return {
    entry,
    geneNames,
    cysteineCount,
    proteinNames
  };
}

export function getGeneNameByProteinId(proteinId: string): string | null {
  if (!searchData || !searchData.p) return null;
  
  // Find the protein by its UniProt ID
  for (const protein of searchData.p) {
    const [entry, , geneNames] = protein;
    if (entry === proteinId) {
      // Return the first gene name if available
      if (geneNames) {
        // Gene names might be in format "GENE1 GENE2" or "GENE1; GENE2"
        const firstGeneName = geneNames.split(/[\s;,]/)[0].trim();
        return firstGeneName || null;
      }
      return null;
    }
  }
  
  return null;
}

export function getProteinNameByProteinId(proteinId: string): string | null {
  if (!searchData || !searchData.p) return null;
  
  // Find the protein by its UniProt ID
  for (const protein of searchData.p) {
    const [entry, , , , proteinNames] = protein;
    if (entry === proteinId) {
      // Return the first protein name if available
      if (proteinNames) {
        // Protein names might have multiple names separated by semicolons
        const firstProteinName = proteinNames.split(';')[0].trim();
        return firstProteinName || null;
      }
      return null;
    }
  }
  
  return null;
}